import {
  TRAINING_FIXED_PAGE_TYPES,
  type TrainingModuleSchema,
  type TrainingPageDef,
  type TrainingQuestionDef,
  type TrainingQuestionGroupDef,
  type TrainingGroupRowDef,
  type TrainingTextGroupDef,
  type TrainingTextGroupRowDef,
} from './types';

/**
 * Derived readers over a module schema. The important one is
 * `trainingQuestions`: it flattens question groups and text groups into
 * their rows, so every consumer (grader, scorer, section roll-up, question
 * bank, reporting) sees one flat list of questions and needs no knowledge
 * of page layout. Follow-up pages contribute nothing here: their questions
 * only exist once a learner has answered, see ./followUp.
 */

/**
 * Expand one group row into the standalone single_choice question it is:
 * the group's shared columns become the row's options, and the group's
 * section carries over so section grading works unchanged.
 */
export function groupRowToQuestion(
  group: TrainingQuestionGroupDef,
  row: TrainingGroupRowDef,
): TrainingQuestionDef {
  return {
    id: row.id,
    type: 'single_choice',
    prompt: row.prompt,
    points: row.points,
    options: group.options,
    ...(row.correct_option_id !== undefined ? { correct_option_id: row.correct_option_id } : {}),
    ...(group.section_id !== undefined ? { section_id: group.section_id } : {}),
    ...(row.optional ? { optional: true } : {}),
  };
}

/**
 * Expand one text-group row into the standalone short_text question it is.
 * No `accepted` list: text groups exist only in unmarked assessments.
 */
export function textGroupRowToQuestion(
  group: TrainingTextGroupDef,
  row: TrainingTextGroupRowDef,
): TrainingQuestionDef {
  return {
    id: row.id,
    type: 'short_text',
    prompt: row.prompt,
    points: row.points,
    ...(row.input !== undefined && row.input !== 'text' ? { input: row.input } : {}),
    ...(row.multiline && (row.input ?? 'text') === 'text' ? { multiline: true } : {}),
    ...(row.placeholder !== undefined ? { placeholder: row.placeholder } : {}),
    ...(group.section_id !== undefined ? { section_id: group.section_id } : {}),
    ...(row.optional ? { optional: true } : {}),
  };
}

/** True for a page that carries one or more authored questions. */
export function isQuestionPage(
  page: TrainingPageDef,
): page is TrainingQuestionGroupDef | TrainingTextGroupDef | TrainingQuestionDef {
  return page.type !== 'slide' && page.type !== 'follow_up';
}

/**
 * True for a page that is not a single question slot (slides, grids, text
 * groups, follow-ups). Typed loosely so the serve logic, which is generic
 * over authoring and player pages, can share it.
 */
export function isFixedPageType(type: string): boolean {
  return (TRAINING_FIXED_PAGE_TYPES as readonly string[]).includes(type);
}

/** The questions of a page, in order. A slide has none, a group has its rows. */
export function pageQuestions(page: TrainingPageDef): TrainingQuestionDef[] {
  switch (page.type) {
    case 'slide':
    case 'follow_up':
      return [];
    case 'question_group':
      return page.rows.map((r) => groupRowToQuestion(page, r));
    case 'text_group':
      return page.rows.map((r) => textGroupRowToQuestion(page, r));
    default:
      return [page];
  }
}

/** The questions of a schema, in page order (slides skipped, groups flattened). */
export function trainingQuestions(schema: TrainingModuleSchema): TrainingQuestionDef[] {
  return schema.pages.flatMap(pageQuestions);
}

/** Total available points across a schema's questions. */
export function trainingTotalPoints(schema: TrainingModuleSchema): number {
  return trainingQuestions(schema).reduce((sum, q) => sum + q.points, 0);
}

/**
 * True when any page is a multi-row or derived page (question group, text
 * group, follow-up). Question banks (serving a random subset, shuffling the
 * order) fill one question slot per page and so cannot express these; the
 * projection and the grader both ignore the serve config for such a schema,
 * and publish-time validation rejects the combination.
 */
export function schemaHasQuestionGroup(schema: TrainingModuleSchema): boolean {
  return schema.pages.some((p) => p.type !== 'slide' && isFixedPageType(p.type));
}
